import { v4 } from 'uuid';
import type { Project } from '../../models/project/project.model';
import {
  PROJECT_EVENT_CLOCK_GROUP_MS,
  projectEventSchema,
  type ProjectEvent,
  type ProjectEventActivity,
  type ProjectEventActor,
} from '../../models/project-event.model';

const isReadableProjectEvent = (value: unknown): value is ProjectEvent =>
  projectEventSchema.safeParse(value).success;

const sameActor = (event: ProjectEvent, actor: ProjectEventActor): boolean =>
  event.actor_id === actor.actor_id &&
  event.actor_kind === actor.actor_kind &&
  event.channel === actor.channel;

/** A deletion that cancels this creation event, if it is one. */
const cancelsCreation = (
  event: ProjectEvent,
  activity: ProjectEventActivity,
): boolean => {
  const created = event.activity;
  switch (activity.kind) {
    case 'building-deleted':
      return (
        created.kind === 'building-created' &&
        created.buildingId === activity.buildingId
      );
    case 'proposal-deleted':
      return (
        created.kind === 'proposal-created' &&
        created.proposalId === activity.proposalId
      );
    case 'version-deleted':
      return (
        created.kind === 'version-created' &&
        created.versionId === activity.versionId
      );
    default:
      return false;
  }
};

/** When the event last changed: appended, or a later edit merged into it. */
export const projectEventChangedAt = (
  event: Pick<ProjectEvent, 'created_at' | 'updated_at'>,
): string => event.updated_at ?? event.created_at;

/** Every storey a footprint edit covers. Older events name only one. */
export const footprintStoreyIds = (activity: {
  storeyId: string;
  storeyIds?: readonly string[];
}): readonly string[] =>
  activity.storeyIds?.length ? activity.storeyIds : [activity.storeyId];

/**
 * Readable events of the clock group that ends at {@link now}, newest first.
 * Like the thread's timestamps, a group runs on while activity continues with
 * less than a clock group between two moments. Activity is when an entry was
 * appended or last had an edit merged in, since a growing entry keeps its
 * first time while the work goes on (DEV-3805).
 */
const clockGroupEvents = (
  events: readonly unknown[],
  now: Date,
): ProjectEvent[] => {
  const readable = events.filter(isReadableProjectEvent);
  const moments = readable
    .flatMap(({ created_at, updated_at }) =>
      updated_at ? [created_at, updated_at] : [created_at],
    )
    .map((moment) => Date.parse(moment))
    .sort((left, right) => right - left);
  let start = now.getTime();
  for (const moment of moments) {
    if (moment > start) continue;
    if (start - moment >= PROJECT_EVENT_CLOCK_GROUP_MS) break;
    start = moment;
  }
  return readable
    .filter((event) => Date.parse(projectEventChangedAt(event)) >= start)
    .reverse();
};

/** The creation that already describes a setup move in this actor's clock group. */
const creationCoveringBuildingMove = (
  events: readonly unknown[],
  event: ProjectEvent,
): ProjectEvent | undefined => {
  if (event.activity.kind !== 'building-moved') return;
  const buildingId = event.activity.buildingId;
  return clockGroupEvents(events, new Date(event.created_at)).find(
    (other) => sameActor(other, event) && createdBuilding(other, buildingId),
  );
};

/** Apply the setup-move display rule to histories recorded before it existed. */
export const withoutBuildingSetupMoves = (
  events: readonly ProjectEvent[],
): readonly ProjectEvent[] => {
  const visible = events.filter(
    (event, index) =>
      event.activity.kind !== 'building-moved' ||
      !creationCoveringBuildingMove(events.slice(0, index), event),
  );
  return visible.length === events.length ? events : visible;
};

/**
 * The same user's entry for the same thing that is still being worked on: it
 * changed in the current clock group ({@link clockGroupEvents}), however many
 * other entries came after it (DEV-3805).
 */
const findOngoing = <T>(
  events: readonly (T | ProjectEvent)[],
  event: ProjectEvent,
  matches: (activity: ProjectEventActivity) => boolean,
): { index: number; ongoing: ProjectEvent } | undefined => {
  const group = new Set<unknown>(
    clockGroupEvents(events, new Date(event.created_at)),
  );
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const ongoing = events[index];
    if (
      group.has(ongoing) &&
      isReadableProjectEvent(ongoing) &&
      matches(ongoing.activity) &&
      sameActor(ongoing, event)
    ) {
      return { index, ongoing };
    }
  }
  return undefined;
};

const replaceAt = <T>(
  events: readonly (T | ProjectEvent)[],
  index: number,
  replacement: ProjectEvent,
): (T | ProjectEvent)[] =>
  events.map((other, otherIndex) =>
    otherIndex === index ? replacement : other,
  );

const createdBuilding = (event: ProjectEvent, buildingId?: string): boolean =>
  event.activity.kind === 'building-created' &&
  (buildingId === undefined || event.activity.buildingId === buildingId);

/**
 * A footprint edit adds nothing the clock group already says: storeys the
 * same user added there, or a building they created there. What is left
 * merges into the same user's ongoing edit of the building
 * ({@link findOngoing}), so a working session on a building stays one entry
 * instead of one per storey and commit (DEV-3805). The entry keeps its place
 * and `updated_at` marks it changed.
 */
const appendFootprintEdit = <T>(
  events: (T | ProjectEvent)[],
  event: ProjectEvent & {
    activity: Extract<
      ProjectEventActivity,
      { kind: 'storey-footprint-edited' }
    >;
  },
): (T | ProjectEvent)[] => {
  const { activity } = event;
  const now = new Date(event.created_at);
  const recent = clockGroupEvents(events, now).filter((other) =>
    sameActor(other, event),
  );
  const created = recent.some((other) =>
    createdBuilding(other, activity.buildingId),
  );
  if (created) return events;
  const added = new Set(
    recent.flatMap((other) =>
      other.activity.kind === 'storey-added' && other.activity.storeyId
        ? [other.activity.storeyId]
        : [],
    ),
  );
  const found = findOngoing(
    events,
    event,
    (other) =>
      other.kind === 'storey-footprint-edited' &&
      other.buildingId === activity.buildingId,
  );
  const continued =
    found?.ongoing.activity.kind === 'storey-footprint-edited'
      ? found.ongoing.activity
      : undefined;
  const storeyIds = [
    ...new Set([
      ...(continued ? footprintStoreyIds(continued) : []),
      ...footprintStoreyIds(activity).filter((id) => !added.has(id)),
    ]),
  ];
  const [storeyId, ...otherStoreyIds] = storeyIds;
  if (!storeyId) return events;
  const merged: ProjectEventActivity = {
    kind: 'storey-footprint-edited',
    buildingId: activity.buildingId,
    storeyId,
    ...(otherStoreyIds.length ? { storeyIds } : {}),
  };
  if (found) {
    return replaceAt(events, found.index, {
      ...found.ongoing,
      activity: merged,
      updated_at: event.created_at,
    });
  }
  return [...events, { ...event, activity: merged }];
};

/**
 * Collapse rules from the comments thread (DEV-3760), applied when an event
 * is appended so the thread only groups rows and never has to hide any
 * (DEV-3805). They apply to the events this client can read, per user and
 * clock group; others are kept as they are.
 * - Creating a building covers what set it up: the project location, added
 *   storeys, footprint edits and setup moves. A location set before the drawing goes.
 * - Deleting what was just created drops both. A building counts as just
 *   created within the clock group; a proposal or version only while its
 *   creation is the newest entry.
 * - The project location shows once per clock group.
 * - Moves and footprint edits of a building merge into the same user's
 *   ongoing entry for it ({@link findOngoing}), which keeps its place and
 *   gets `updated_at`.
 */
const appendOne = <T>(
  events: (T | ProjectEvent)[],
  event: ProjectEvent,
): (T | ProjectEvent)[] => {
  const { activity } = event;
  const now = new Date(event.created_at);
  const recentBySameActor = (): ProjectEvent[] =>
    clockGroupEvents(events, now).filter((other) => sameActor(other, event));
  const without = (dropped: readonly ProjectEvent[]): (T | ProjectEvent)[] => {
    if (!dropped.length) return events;
    const droppedSet = new Set<unknown>(dropped);
    return events.filter((other) => !droppedSet.has(other));
  };
  switch (activity.kind) {
    case 'storey-footprint-edited':
      return appendFootprintEdit(events, { ...event, activity });
    case 'project-location': {
      const covered = recentBySameActor().some(
        (other) =>
          other.activity.kind === 'project-location' || createdBuilding(other),
      );
      if (covered) return events;
      break;
    }
    case 'storey-added': {
      const covered = recentBySameActor().some((other) =>
        createdBuilding(other, activity.buildingId),
      );
      if (covered) return events;
      break;
    }
    case 'building-created': {
      const locations = recentBySameActor().filter(
        (other) => other.activity.kind === 'project-location',
      );
      return [...without(locations), event];
    }
    case 'building-moved': {
      const creation = creationCoveringBuildingMove(events, event);
      if (creation) {
        return replaceAt(events, events.indexOf(creation), {
          ...creation,
          updated_at: projectEventChangedAt(event),
        });
      }
      const found = findOngoing(
        events,
        event,
        (other) =>
          other.kind === 'building-moved' &&
          other.buildingId === activity.buildingId,
      );
      if (!found) break;
      return replaceAt(events, found.index, {
        ...found.ongoing,
        updated_at: event.created_at,
      });
    }
    case 'building-deleted': {
      const creation = recentBySameActor().find((other) =>
        cancelsCreation(other, activity),
      );
      if (creation) return without([creation]);
      break;
    }
    case 'proposal-deleted':
    case 'version-deleted': {
      const newest = events[events.length - 1];
      if (isReadableProjectEvent(newest) && cancelsCreation(newest, activity)) {
        return events.slice(0, -1);
      }
      break;
    }
    default:
      break;
  }
  return [...events, event];
};

/**
 * Record {@link activities} on the project. Returns the same project when
 * there is nothing to record.
 */
export const appendProjectEvents = (
  project: Project,
  activities: readonly ProjectEventActivity[],
  actor: ProjectEventActor,
  now: Date = new Date(),
): Project => {
  if (!activities.length) return project;
  const events = activities.reduce<ProjectEvent[]>(
    (current, activity) =>
      appendOne(current, {
        id: v4(),
        ...actor,
        activity,
        created_at: now.toISOString(),
      }),
    project.events ?? [],
  );
  return { ...project, events };
};

/**
 * Replay a stored history through the collapse rules, as if each event had
 * been appended at its own time. Folds histories recorded before the rules
 * merged footprint edits and building moves (DEV-3805). Events this client
 * cannot read are kept in place.
 */
export const compactProjectEvents = (events: readonly unknown[]): unknown[] =>
  events.reduce<unknown[]>(
    (current, event) =>
      isReadableProjectEvent(event)
        ? appendOne(current, event)
        : [...current, event],
    [],
  );

/** First occurrence of each {@link ProjectEvent.id}. */
export const dedupeProjectEvents = (
  events: readonly ProjectEvent[],
): ProjectEvent[] => {
  const seen = new Set<string>();
  return events.filter((event) => {
    if (seen.has(event.id)) return false;
    seen.add(event.id);
    return true;
  });
};

/** The project without its history, for shared links and report generation. */
export const withoutProjectEvents = <T extends Project>(project: T): T => ({
  ...project,
  events: [],
});

/**
 * The events the thread can show. Saves store the list as sent, so an event
 * of a kind this client does not know, or a malformed one, is skipped rather
 * than breaking the panel. Older events stay readable: new fields are
 * optional. Returns the same list when every event is readable.
 */
export const readableProjectEvents = (
  events: readonly unknown[],
): readonly ProjectEvent[] =>
  events.every(isReadableProjectEvent)
    ? events
    : events.filter(isReadableProjectEvent);
